import firestore from "@/firebase/db";
// import { adminDb } from '@/lib/firebase/firebaseAdmin';
import Monitoring from "@/utils/monitoring";
import { isUserBuyer, isUserRealtor } from "@/utils/profile";
import { RoleChangeRequest, UserProfile } from "@/utils/type";
import { getAuth } from "firebase/auth";
import { Timestamp } from "firebase/firestore";
import { collection, query, where, getDocs } from 'firebase/firestore';


const collectionName = "profile";
const roleChangeCollection = "roleChangeRequests";

interface ProfileService {
  fetchUserProfileByEmail(email: string): Promise<UserProfile | null>;
  updateUserProfile(
    uid: string,
    profileData: Partial<UserProfile>
  ): Promise<void>;
  submitRoleChangeRequest(
    uid: string,
    profileData: Partial<UserProfile>,
    role: "Realtor",
    address: string,
    email: string,
    isResubmission: boolean,
    rejectionReason: string | null
  ): Promise<void>;
  getRoleChangeRequests(): Promise<RoleChangeRequest[]>;
  getAllUserProfiles(): Promise<UserProfile[]>;
  // getAllUserProfilesfromAdmin(): Promise<UserProfile[]>;
  getAdminStats(): Promise<{
    totalUsers: number;
    totalRealtors: number;
    totalBuyers: number;
  }>;
  approveRoleChangeRequest(
    userId: string,
    email: string,
    requestedRole: "Realtor" | "Buyer",
    requestId: string
  ): Promise<void>;
  rejectRoleChangeRequest(
    userId: string,
    email: string,
    requestId: string,
    rejectionReason: string
  ): Promise<void>;
  setSpecialRequirementByEmail(
    email: string,
    requirement: string
  ): Promise<void>;
  checkAndClearSpecialRequirementNotification(
    email: string
  ): Promise<string | null | any>;
  changeUserRole(userId: string, newRole: string): Promise<void>;
  toggleBlogWriterStatus(userId: string, isBlogWriter: boolean): Promise<void>;
  changeSuperAdminRole(email: string, newRole: string): Promise<void>;
  searchUsersByRole(role: string, searchTerm: string): Promise<UserProfile[]>;
}

export const profileService: ProfileService = {
  // Fetch user profile data by email
  async fetchUserProfileByEmail(email: string): Promise<UserProfile | null> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const userProfiles = await firestore.get(collectionName, {
          where: [["email", "==", email]],
        });
        // console.log("userProfiles", userProfiles)
        if (userProfiles.length > 0) {
          return userProfiles[0] as UserProfile;
        } else {
          return null;
        }
      } catch (error: any) {
        Monitoring.trackError(error as Error, {
          function: "fetchUserProfileByEmail",
          email,
          collection: collectionName,
          userId: currentUser.uid,
          errorCode: error.code,
        });
        throw error;
      }
    }
    return null;
  },
  // Update user profile data by UID
  async updateUserProfile(
    uid: string,
    profileData: Partial<UserProfile>
  ): Promise<void> {
    if (!uid) {
      throw new Error("User ID is required");
    }
    // Try to find an existing profile by userId first
    let existingProfile = null;
    const profilesByUserId = await firestore.get(collectionName, {
      where: [["userId", "==", uid]],
    });
    console.log("profilesByUserId", profilesByUserId);
    if (profilesByUserId.length > 0) {
      existingProfile = profilesByUserId[0];
    } else if (profileData?.email) {
      // Fallback: try by email if provided
      existingProfile = await this.fetchUserProfileByEmail(profileData.email);
    }
    try {
      // Clean up the profile data to remove any undefined or null values
      const cleanedProfileData = Object.entries(profileData).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, any>
      );

      // Set default status if not provided
      if (!cleanedProfileData.status) {
        cleanedProfileData.status = isUserRealtor(cleanedProfileData.userRole)
          ? "pending"
          : "approved";
      }

      // Set updatedAt timestamp
      cleanedProfileData.updatedAt = Timestamp.now();

      if (existingProfile) {
        // Ensure userId is preserved and valid
        const updateData = {
          ...cleanedProfileData,
          userId: existingProfile.userId || uid, // Use existing userId or provided uid as fallback
        };

        await firestore.update(
          `${collectionName}/${existingProfile.id}`,
          updateData
        );
      } else {
        // Create new profile with required userId
        const newProfileData = {
          ...cleanedProfileData,
          userId: uid,
          createdAt: Timestamp.now(),
        };

        await firestore.create(collectionName, newProfileData);
      }
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "updateUserProfile",
        uid,
        email: profileData?.email,
        collection: collectionName,
        isUpdate: !!existingProfile,
        updatedFields: Object.keys(profileData),
      });
      throw error;
    }
  },

  // Handle role change requests
  async submitRoleChangeRequest(
    uid: string,
    profileData: Partial<UserProfile>,
    role: "Realtor",
    address: string,
    email: string,
    isResubmission: boolean,
    rejectionReason: string | null
  ): Promise<void> {
    try {
      // Update user profile
      await this.updateUserProfile(uid, {
        userRole: role,
        status: role === "Realtor" ? "pending" : "approved",
        roleChangeRequested: role === "Realtor" ? true : false,
        ...profileData,
      });

      // Fetch updated user profile to get email and names
      const updatedProfile = await this.fetchUserProfileByEmail(
        email as string
      );
      if (!updatedProfile) {
        throw new Error("User profile not found after update.");
      }

      // Create role change request
      const roleChangeRequest: RoleChangeRequest = {
        userId: uid,
        firstName: updatedProfile.firstName || "",
        lastName: updatedProfile.lastName || "",
        email,
        requestedRole: role,
        status: "pending",
        createdAt: Timestamp.now(),
        address,
        isResubmission,
        previousRejectionReason: rejectionReason,
      };

      await firestore.create(roleChangeCollection, roleChangeRequest);
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "submitRoleChangeRequest",
        uid,
        email,
        requestedRole: role,
        isResubmission,
        hasRejectionReason: !!rejectionReason,
      });
      throw error;
    }
  },

  async getRoleChangeRequests(): Promise<RoleChangeRequest[]> {
    try {
      const requests = await firestore.get(roleChangeCollection, {
        where: [["status", "==", "pending"]],
      });
      return requests as RoleChangeRequest[];
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "getRoleChangeRequests",
        collection: roleChangeCollection,
      });
      throw error;
    }
  },

  // Fetch all user profiles
  async getAllUserProfiles(): Promise<UserProfile[]> {
    try {
      const profiles = await firestore.get(collectionName, {});
      return profiles as UserProfile[];
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "getAllUserProfiles",
        collection: collectionName,
      });
      throw error;
    }
  },

  // Get admin stats
  async getAdminStats(): Promise<{
    totalUsers: number;
    totalRealtors: number;
    totalBuyers: number;
  }> {
    try {
      const users = await this.getAllUserProfiles();
      const stats = users.reduce(
        (acc, user) => {
          acc.totalUsers++;
          if (isUserRealtor(user.userRole)) acc.totalRealtors++;
          if (isUserBuyer(user.userRole)) acc.totalBuyers++;
          return acc;
        },
        { totalUsers: 0, totalRealtors: 0, totalBuyers: 0 }
      );
      return stats;
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "getAdminStats",
        collection: collectionName,
      });
      throw error;
    }
  },

  async approveRoleChangeRequest(
    userId: string,
    email: string,
    requestedRole: "Realtor" | "Buyer",
    requestId: string
  ): Promise<void> {
    try {
      await this.updateUserProfile(userId, {
        email,
        userRole: requestedRole,
        roleChangeRequested: false,
        isUnderReview: false,
        status: "approved",
      });
      await firestore.update(`${roleChangeCollection}/${requestId}`, {
        status: "approved",
      });
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "approveRoleChangeRequest",
        userId,
        email,
        requestedRole,
        requestId,
        collection: roleChangeCollection,
      });
      throw error;
    }
  },

  async rejectRoleChangeRequest(
    userId: string,
    email: string,
    requestId: string,
    rejectionReason: string
  ): Promise<void> {
    try {
      // console.log('roleChangeRequests', userId);
      // console.log('requestId', requestId);

      // Update the user profile
      await this.updateUserProfile(userId, {
        email,
        roleChangeRequested: false,
        status: "rejected",
        rejectionReason,
        isUnderReview: false,
      });

      // Update the role change request
      await firestore.update(`${roleChangeCollection}/${requestId}`, {
        status: "rejected",
        rejectionReason,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "rejectRoleChangeRequest",
        userId,
        email,
        requestId,
        hasRejectionReason: !!rejectionReason,
        collection: roleChangeCollection,
      });
      throw error;
    }
  },

  // Set special requirement for a user by email
  async setSpecialRequirementByEmail(
    email: string,
    requirement: string
  ): Promise<void> {
    try {
      const userProfiles = await firestore.get(collectionName, {
        where: [["email", "==", email]],
      });
      if (userProfiles.length === 0) {
        throw new Error("User not found");
      }

      const userProfile = userProfiles[0] as UserProfile;
      // Update the user profile with the special requirement
      await this.updateUserProfile(userProfile?.userId, {
        missingInformation: requirement,
        specialRequirementNotification: true,
      });
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "setSpecialRequirementByEmail",
        email,
        hasRequirement: !!requirement,
        collection: collectionName,
      });
      throw error;
    }
  },

  async checkAndClearSpecialRequirementNotification(
    email: string
  ): Promise<string | null | any> {
    let userProfile: UserProfile | null = null;
    try {
      userProfile = await this.fetchUserProfileByEmail(email);
      if (userProfile && userProfile.specialRequirementNotification) {
        const message = userProfile.missingInformation;
        // await firestore.update(`${collectionName}/${userId}`, {
        //   specialRequirementNotification: true
        // });
        return message;
      }
      return null;
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "checkAndClearSpecialRequirementNotification",
        email,
        collection: collectionName,
        hasUserProfile: !!userProfile,
      });
      throw error;
    }
  },

  // Add changeUserRole function
  async changeUserRole(email: string, newRole: string): Promise<void> {
    if (!email || !newRole) {
      throw new Error("Email and new role are required");
    }

    try {
      // First, get the current user profile
      const userProfile = await this.fetchUserProfileByEmail(email);

      console.log("userProfile", userProfile);
      if (!userProfile) {
        throw new Error("User profile not found");
      }
      // Update the user profile with new role
      await firestore.update(`${collectionName}/${userProfile?.id}`, {
        userRole: newRole,
        roleChangeRequested: false,
        isUnderReview: false,
        updatedAt: Timestamp.now(),
      });

      // If there's an active role change request, update it
      const roleChangeRequests = await firestore.get(roleChangeCollection, {
        where: [
          ["email", "==", email],
          ["status", "==", "pending"],
        ],
      });

      console.log("roleChangeRequests", roleChangeRequests);
      if (roleChangeRequests.length > 0) {
        await firestore.update(
          `${roleChangeCollection}/${roleChangeRequests[0].id}`,
          {
            status: "approved",
            updatedAt: Timestamp.now(),
          }
        );
      }
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "changeUserRole",
        email,
        newRole,
        collection: collectionName,
      });
      throw error;
    }
  },

  async toggleBlogWriterStatus(userId: string, isBlogWriter: boolean): Promise<void> {
    try {
      await this.updateUserProfile(userId, {
        isBlogWriter,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "toggleBlogWriterStatus",
        userId,
        isBlogWriter,
        collection: collectionName,
      });
      throw error;
    }
  },

  async changeSuperAdminRole(email: string, newRole: string): Promise<void> {
    if (!email || !newRole) {
      throw new Error("Email and new role are required");
    }

    try {
      // Get the user profile by email
      const userProfile = await this.fetchUserProfileByEmail(email);

      if (!userProfile) {
        throw new Error("User profile not found");
      }

      // Update the user profile with new role
      await firestore.update(`${collectionName}/${userProfile.id}`, {
        userRole: newRole,
        roleChangeRequested: false,
        isUnderReview: false,
        updatedAt: Timestamp.now(),
      });

      // If there's an active role change request, update it
      const roleChangeRequests = await firestore.get(roleChangeCollection, {
        where: [
          ["email", "==", email],
          ["status", "==", "pending"],
        ],
      });

      if (roleChangeRequests.length > 0) {
        await firestore.update(
          `${roleChangeCollection}/${roleChangeRequests[0].id}`,
          {
            status: "approved",
            updatedAt: Timestamp.now(),
          }
        );
      }
    } catch (error) {
      Monitoring.trackError(error as Error, {
        function: "changeSuperAdminRole",
        email,
        newRole,
        collection: collectionName,
      });
      throw error;
    }
  },

  async searchUsersByRole(role: string, searchTerm: string): Promise<UserProfile[]> {
    try {
      const profiles = await firestore.get(collectionName, {
        where: [["userRole", "==", role]]
      });
      
      return profiles.filter((profile: any) => {
        const userData = profile as UserProfile;
        const searchTermLower = searchTerm.toLowerCase();
        
        // Format phone number for comparison (remove spaces, dashes, etc.)
        const formatPhone = (phone: string | undefined) => {
          if (!phone) return '';
          return phone.replace(/[\s\-\(\)]/g, '');
        };

        return (
          userData.email?.toLowerCase().includes(searchTermLower) ||
          formatPhone(userData.phoneNumber)?.includes(formatPhone(searchTerm)) ||
          userData.firstName?.toLowerCase().includes(searchTermLower) ||
          userData.lastName?.toLowerCase().includes(searchTermLower)
        );
      }) as UserProfile[];
    } catch (error) {
      console.error('Error searching users by role:', error);
      throw error;
    }
  },
};

export default profileService;
